import { createDidomiAdapter } from './cmp/didomi'
import { createSourcepointAdapter } from './cmp/sourcepoint'
import type { CmpAdapter, CmpDeps } from './cmp/types'
import { hasUnpoly, unpolyBooted } from 'shared/javascripts/util/unpoly_compat'
import { on } from 'shared/javascripts/util/events'

// CMP-agnostic facade. Each CMP lives in cmp/<name>.ts and implements CmpAdapter; initGdpr() selects
// one based on window.gdprConsentConfig.mode (set in _gdpr_consent.html.erb) and the exported functions
// delegate to it. Adding a CMP = a new cmp/<name>.ts + a mode + an embed branch; consumers are unchanged.

let bannerFired = false
let consentReady = false
let activeAdapter: CmpAdapter | null = null

const CONSENT_CHANGED_EVENT = 'gdpr:vendor-grant-changed'

// getConsentString is CMP-agnostic: SourcePoint and Didomi both expose the IAB TCF API (__tcfapi).
export function getConsentString(): Promise<string | undefined> {
  return new Promise((resolve) => {
    if (typeof window.__tcfapi !== 'function') {
      resolve(undefined)
      return
    }

    // Guard against the IAB stub queueing the call forever when the SDK never loads (e.g. blocked):
    // getConsentString only feeds an optional ad-tag parameter, so falling back to undefined is fine.
    let settled = false
    const finish = (value: string | undefined): void => {
      window.clearTimeout(timeout)
      if (settled) return
      settled = true
      resolve(value)
    }

    const timeout = window.setTimeout(() => {
      console.error(
        '[CMP] __tcfapi addEventListener did not respond within 2s; ad tags will omit the consent string',
      )
      finish(undefined)
    }, 2000)

    // addEventListener calls back right away with the current TCData and again on every change, so we
    // take the first answer and unregister.
    window.__tcfapi('addEventListener', 2, (tcData, success) => {
      if (!success || !tcData) {
        finish(undefined)
        return
      }

      finish(tcData.tcString)
      window.__tcfapi!('removeEventListener', 2, () => {}, tcData.listenerId)
    })
  })
}

export function hasVendorGrant(vendorName: string): boolean {
  return activeAdapter?.hasVendorGrant(vendorName) ?? false
}

export function gdprSettingsAvailable(): boolean {
  return (
    typeof window !== 'undefined' && (window.gdprConsentConfig?.mode ?? 'disabled') !== 'disabled'
  )
}

// Reopens the consent/preferences UI. `privacyManagerId` is only used by CMPs that need it (SourcePoint).
export function openGdprManager(privacyManagerId: string): void {
  activeAdapter?.openGdprManager(privacyManagerId)
}

// Schedules a callback for when GDPR consent is available (i.e. the user resolved the banner, or the
// CMP loaded an existing decision from storage). If consent is already available, the callback runs in
// the next microtask. `bannerFired` reports whether a consent banner was shown this session.
export function onConsentReady(callback: (data: { bannerFired: boolean }) => void): () => void {
  const fireCallback = (): void => {
    callback({ bannerFired })
  }

  if (consentReady) {
    queueMicrotask(fireCallback)
    return () => {}
  } else {
    document.addEventListener(CONSENT_CHANGED_EVENT, fireCallback, { once: true })
    return () => document.removeEventListener(CONSENT_CHANGED_EVENT, fireCallback)
  }
}

// --- Dependencies handed to the active adapter (so adapters never import this facade) ---

// Marks consent as resolved and notifies consumers (gtag, Convert, blockable iframes); re-emits on
// every change so listeners react to later consent changes too.
function signalConsentChanged(): void {
  consentReady = true
  document.dispatchEvent(new CustomEvent(CONSENT_CHANGED_EVENT))
}

function markBannerShown(): void {
  bannerFired = true
}

// Runs `onAllowed` once the `gdpr-consent` meta tag allows messaging. On delay pages (terms/imprint/
// privacy) it starts as `disallow`, so we wait until the user navigates to a page that allows it.
function whenMessagingAllowed(onAllowed: () => void): void {
  function allowed(): boolean {
    return (
      document.head.querySelector('meta[name="gdpr-consent"]')?.getAttribute('content') === 'allow'
    )
  }

  function runOnceAllowed(): void {
    if (allowed()) {
      onAllowed()
      return
    }

    const observer = new MutationObserver(() => {
      if (allowed()) {
        observer.disconnect()
        onAllowed()
      }
    })
    observer.observe(document.head, {
      childList: true,
      subtree: true,
      attributeFilter: ['content'],
    })
  }

  if (hasUnpoly() && !unpolyBooted()) {
    const stopWaiting = on('up:framework:booted', () => {
      stopWaiting()
      runOnceAllowed()
    })
  } else {
    runOnceAllowed()
  }
}

export function initGdpr(): void {
  const mode = window.gdprConsentConfig?.mode ?? 'disabled'
  const deps: CmpDeps = { whenMessagingAllowed, signalConsentChanged, markBannerShown }

  activeAdapter =
    mode === 'sourcepoint' ? createSourcepointAdapter(deps) : createDidomiAdapter(deps, mode)

  activeAdapter.init()

  window._gdpr_test = {
    getConsentString,
    hasVendorGrant,
    onConsentReady,
  }
}
